import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useServerSideFacetExpansion } from '@/pages/Search/SearchPage/hooks/useServerSideFacetExpansion';
import { CurrentOpenMenu, STANDARD_VIEW_MORE_SIZE } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { getBrowseDimensionKeyFromFacetType } from '@/utils/browsePath/browsePath';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import { isFacetOptionChecked } from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

import { FacetCard } from '../FacetCard/FacetCard';

/** Same shape as entries from `getSearchFacets` / `getMergedOptionFacet` (id + options). */
type MergedOptionFacetData = {
    options?: FacetOption[];
};

type Props = {
    facetId: string;
    mergedOptionFacet: MergedOptionFacetData;
    title: string;
};

/** Checkbox list for one dynamic attribute facet (`dynamic_attr_*`). Options come from the parent via `mergedOptionFacet`. */
export const AttributeFacetFilter = ({ facetId, mergedOptionFacet, title }: Props) => {
    const { isLoading, onViewMoreClick } = useServerSideFacetExpansion({
        facetId,
        size: STANDARD_VIEW_MORE_SIZE,
    });
    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const { options = [] } = mergedOptionFacet;
    const isOptionChecked = useCallback(
        (option: FacetOption) =>
            isFacetOptionChecked(pathname, queryParams as Record<string, unknown>, facetId, option, option.selected),
        [pathname, queryParams, facetId]
    );

    const onOptionClick = (option: FacetOption) => {
        const newCheckStatus = !isOptionChecked(option);
        const isSemanticBrowseFacet = Boolean(getBrowseDimensionKeyFromFacetType(facetId));
        const valueForUrl = isSemanticBrowseFacet ? urlSafeTitle(option.name ?? String(option.id)) : option.id;

        let newParams = modifyParamsWhenChecked(queryParams, facetId, valueForUrl, newCheckStatus, {
            useSemanticAttributeParams: true,
        });
        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.AttrFacetChanged : FilterAction.AttrFacetRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(
        () => options.filter((option) => option.count !== 0 || isOptionChecked(option)),
        [options, isOptionChecked]
    );

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <FacetCard
            headline={title}
            isLoading={isLoading}
            onViewMoreClick={onViewMoreClick}
        >
            {filteredOptions.map((option) => (
                <Checkbox.Option
                    checked={isOptionChecked(option)}
                    disabled={option.count === 0 && !isOptionChecked(option)}
                    key={option.id}
                    onChange={() => {
                        onOptionClick(option);
                    }}
                >
                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                </Checkbox.Option>
            ))}
        </FacetCard>
    );
};
